import { Checkbox, InlineBanner, Input, Radio, Select } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  chooseEndDateMode,
  END_DATE_ORDER_ERROR,
  isEndDateBeforeStart,
  parseUnlimitedInt,
  type typeSurvivorRulesDraft,
} from "./survivorRulesDraft";

export const LOSS_POINTS_WARNING =
  "Heads up, a missed pick counts as a loss. With loss points above zero, a player who never picks still scores.";

const WEEK_START_ITEMS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "7", label: "Sunday" },
] as const;

// ISSUE-1095. TBD is the default: the contest runs until one survivor is
// left or the season ends.
const END_DATE_MODE_ITEMS = [
  {
    value: "tbd",
    label: "TBD",
    description: "Runs until one survivor is left or the season ends.",
  },
  { value: "date", label: "Pick a date" },
] as const;

const ROUND_GROUPING_ITEMS = [
  { value: "week", label: "By week" },
  { value: "day", label: "By game day" },
] as const;

const LOCK_ITEMS = [
  {
    value: "firstGame",
    label: "When the first game of the round starts",
    description: "Everything locks for everyone at the same moment.",
  },
  {
    value: "lastGame",
    label: "When the last game of the round starts",
    description: "Each pick locks when its own game kicks off.",
  },
] as const;

const PICK_BASIS_ITEMS = [
  { value: "moneyline", label: "Pick the winner (moneyline)" },
  { value: "spread", label: "Beat the spread" },
] as const;

const SPREAD_TIMING_ITEMS = [
  {
    value: "live",
    label: "Live",
    description:
      "Each spread keeps moving until that game starts or the round locks, whichever comes first.",
  },
  {
    value: "lockedEarly",
    label: "Locked early",
    description:
      "Every spread in the round is fixed once the round's first game is under 24 hours away.",
  },
] as const;

const ADVANCE_ITEMS = [
  { value: "winAll", label: "Win them all" },
  {
    value: "winHalfOrMore",
    label: "Win half or more",
    description: "Half counts. 2 out of 4 gets you through.",
  },
] as const;

const TIE_ITEMS = [
  { value: "tieIsLoss", label: "Counts as a loss" },
  { value: "tieIsWin", label: "Counts as a win" },
] as const;

const POSTPONE_ITEMS = [
  {
    value: "makeUpNextRound",
    label: "Make it up next round",
    description:
      "If a game is called off or moved to another day, that pick is thrown out and the player gets a free one.",
  },
  {
    value: "countAsLoss",
    label: "Counts as a loss",
    description:
      "If a game is called off, that pick is a loss. That includes a game that just gets moved to another day.",
  },
] as const;

const REVIVES_ITEMS = [
  { value: "unlimited", label: "Unlimited" },
  { value: "limited", label: "A set number" },
] as const;

const TIEBREAK_ITEMS = [
  { value: "splitMedal", label: "Split the medal" },
  { value: "noWinner", label: "No winner" },
  { value: "commissionerPick", label: "Commissioner's pick" },
] as const;

export const RULES_NUMBER_REQUIRED_ERROR = "Enter a number.";

export function rulesNumberMinError(min: number): string {
  return `Enter a number of ${min} or more.`;
}

export type typeCreatePoolRulesStepProps = {
  rules: typeSurvivorRulesDraft;
  onChangeRules: (rules: typeSurvivorRulesDraft) => void;
  /** False hides Max members, which is a pool setting and not part of a
   *  contest config. The New contest dialog (ISSUE-1042) and the edit dialog
   *  (ISSUE-1041) pass false; /contest/update does not carry it. Defaults to
   *  true. */
  isMaxMembersShown?: boolean | undefined;
  /** A string disables both date inputs and renders this note under them.
   *  Null or absent keeps the dates editable. */
  datesLockedNote?: string | null | undefined;
};

type typeRulesNumberInputProps = {
  label: string;
  hint?: string | undefined;
  placeholder?: string | undefined;
  /** The lowest number this field takes. Leave it off when any whole number works. */
  min?: number | undefined;
  /**
   * True when the step cannot continue while this field sits empty. False for a
   * field where empty is a real answer, which in this step means unlimited.
   */
  required?: boolean | undefined;
  value: number | null;
  onChangeValue: (value: number | null) => void;
  testId?: string | undefined;
};

/**
 * One numeric rule field. Every number in this step goes through here, which is
 * the point of it.
 *
 * The old code repeated the same onChange at all ten numeric fields, and every
 * copy threw the empty string away. A fan who selected the contents and pressed
 * delete watched the old digit jump right back in, because state never changed
 * and React re-rendered the field from the number it still held (ISSUE-903).
 *
 * What this handler does instead:
 * - An empty box reaches the draft as null, so the field really does clear. For
 *   a required field the draft is then incomplete, which switches Continue off
 *   the same way a blank pool name does on the basics step.
 * - A number below `min` is turned away and never reaches the draft, so nobody
 *   saves a pool with 0 lives. The Input error slot shows `rulesNumberMinError(min)`.
 * - So is anything that is not a whole number. Turning it away leaves the box
 *   alone rather than rewriting it, so a half typed minus sign survives.
 */
const RulesNumberInput = (props: typeRulesNumberInputProps): ReactNode => {
  const [blankError, setBlankError] = useState<string | null>(null);
  const [minError, setMinError] = useState<string | null>(null);
  const required = props.required ?? false;
  const min = props.min;

  return (
    <Input
      label={props.label}
      hint={props.hint}
      placeholder={props.placeholder}
      type="number"
      inputMode="numeric"
      min={min}
      value={props.value === null ? "" : String(props.value)}
      error={minError ?? blankError ?? undefined}
      onChange={(event) => {
        // `parseUnlimitedInt` is this feature's blank tolerant integer parser:
        // it reports an empty box as a value of null instead of as a failure to
        // parse. Every field wants that read. The two unlimited fields take the
        // null as the fan's answer; the required ones take it as a gap.
        const parsed = parseUnlimitedInt(event.target.value);
        if (!parsed.ok) return;
        if (parsed.value !== null && min !== undefined && parsed.value < min) {
          setMinError(rulesNumberMinError(min));
          setBlankError(null);
          return;
        }
        setMinError(null);
        setBlankError(null);
        props.onChangeValue(parsed.value);
      }}
      onBlur={() => {
        setBlankError(
          required && props.value === null ? RULES_NUMBER_REQUIRED_ERROR : null,
        );
        if (props.value !== null) {
          setMinError(null);
        }
      }}
      data-testid={props.testId}
    />
  );
};

export const CreatePoolRulesStep = (
  props: typeCreatePoolRulesStepProps,
): ReactNode => {
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const rules = props.rules;
  const isMaxMembersShown = props.isMaxMembersShown ?? true;
  const datesLockedNote = props.datesLockedNote ?? null;
  const areDatesLocked = datesLockedNote !== null;

  function setRules(next: typeSurvivorRulesDraft): void {
    props.onChangeRules(next);
  }

  return (
    <div className="flex flex-col gap-6" data-testid="create-pool-step-rules">
      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">When it runs</h2>
        <Input
          label="Start date"
          type="date"
          value={rules.startDate}
          disabled={areDatesLocked}
          onChange={(event) => {
            setEndDateError(null);
            setRules({ ...rules, startDate: event.target.value });
          }}
          data-testid="create-pool-start-date"
        />
        <div data-testid="create-pool-end-date-mode">
          <Radio
            label="End date"
            items={END_DATE_MODE_ITEMS}
            value={rules.endDateMode}
            disabled={areDatesLocked}
            onValueChange={(next) => {
              setEndDateError(null);
              setRules(
                chooseEndDateMode(rules, next === "date" ? "date" : "tbd"),
              );
            }}
          />
        </div>
        {rules.endDateMode === "date" ? (
          <Input
            label="Last day of the contest"
            type="date"
            value={rules.endDate}
            disabled={areDatesLocked}
            error={endDateError ?? undefined}
            onChange={(event) => {
              setEndDateError(null);
              setRules({ ...rules, endDate: event.target.value });
            }}
            onBlur={() => {
              if (isEndDateBeforeStart(rules.startDate, rules.endDate)) {
                setEndDateError(END_DATE_ORDER_ERROR);
              }
            }}
            data-testid="create-pool-end-date"
          />
        ) : null}
        {datesLockedNote !== null ? (
          <p
            className="text-caption text-ink-secondary"
            data-testid="create-pool-dates-locked"
          >
            {datesLockedNote}
          </p>
        ) : null}
        <Radio
          label="How rounds are grouped"
          orientation="horizontal"
          items={ROUND_GROUPING_ITEMS}
          value={rules.roundGrouping}
          onValueChange={(next) => {
            setRules({
              ...rules,
              roundGrouping: next === "day" ? "day" : "week",
            });
          }}
        />
        {rules.roundGrouping === "week" ? (
          <Select
            label="Week starts on"
            items={[...WEEK_START_ITEMS]}
            value={String(rules.weekStartDay)}
            onValueChange={(next) => {
              setRules({
                ...rules,
                weekStartDay: Number(
                  next,
                ) as typeSurvivorRulesDraft["weekStartDay"],
              });
            }}
          />
        ) : null}
        <Radio
          label="When picks lock"
          items={LOCK_ITEMS}
          value={rules.roundLockTiming}
          onValueChange={(next) => {
            setRules({
              ...rules,
              roundLockTiming: next === "lastGame" ? "lastGame" : "firstGame",
            });
          }}
        />
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">How picking works</h2>
        <RulesNumberInput
          label="Picks per round"
          min={1}
          required
          value={rules.requiredPicksPerRound}
          onChangeValue={(value) => {
            setRules({ ...rules, requiredPicksPerRound: value });
          }}
        />
        <Radio
          label="Pick basis"
          orientation="horizontal"
          items={PICK_BASIS_ITEMS}
          value={rules.pickBasis}
          onValueChange={(next) => {
            setRules({
              ...rules,
              pickBasis: next === "spread" ? "spread" : "moneyline",
            });
          }}
        />
        {rules.pickBasis === "spread" ? (
          <div data-testid="create-pool-spread-timing">
            <Radio
              label="Spread timing"
              items={SPREAD_TIMING_ITEMS}
              value={rules.spreadTiming}
              onValueChange={(next) => {
                setRules({
                  ...rules,
                  spreadTiming: next === "lockedEarly" ? "lockedEarly" : "live",
                });
              }}
            />
          </div>
        ) : null}
        <Checkbox
          label="Can a team be picked again?"
          description="Off means each team can only be used once in this contest."
          checked={rules.teamReuseAllowed}
          onCheckedChange={(checked) => {
            setRules({ ...rules, teamReuseAllowed: checked === true });
          }}
        />
        {rules.teamReuseAllowed ? (
          <>
            <RulesNumberInput
              label="Days before a team can be reused"
              hint="0 means they can be used again the next round."
              min={0}
              required
              value={rules.teamReuseLockoutDays}
              onChangeValue={(value) => {
                setRules({ ...rules, teamReuseLockoutDays: value });
              }}
              testId="create-pool-team-reuse-lockout"
            />
            <RulesNumberInput
              label="How many times can a team be used"
              hint="Leave blank for unlimited."
              placeholder="Unlimited"
              min={1}
              value={rules.teamReuseMaxCount}
              onChangeValue={(value) => {
                setRules({ ...rules, teamReuseMaxCount: value });
              }}
              testId="create-pool-team-reuse-cap"
            />
          </>
        ) : null}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">How you survive</h2>
        <Radio
          label="To survive a round"
          orientation="horizontal"
          items={ADVANCE_ITEMS}
          value={rules.advanceCriterion}
          onValueChange={(next) => {
            setRules({
              ...rules,
              advanceCriterion:
                next === "winHalfOrMore" ? "winHalfOrMore" : "winAll",
            });
          }}
        />
        <RulesNumberInput
          label="Lives before elimination"
          hint={
            rules.advanceCriterion === "winHalfOrMore"
              ? "Fail a round and it costs one life, however many picks you got wrong in it. Pass the round and it costs nothing. Go 2 of 3 and you passed the round. That costs you nothing, even though one pick lost."
              : "Fail a round and it costs one life, however many picks you got wrong in it. Pass the round and it costs nothing."
          }
          min={1}
          required
          value={rules.livesAllowed}
          onChangeValue={(value) => {
            setRules({ ...rules, livesAllowed: value });
          }}
        />
        <RulesNumberInput
          label="Skips allowed"
          hint="A skip lets a player sit a round out on purpose. They survive it, they score nothing for it, and their win streak stays intact. A skip covers a whole round. A player who gets some picks in but not all of them has not skipped, and the ones they missed count as losses."
          min={0}
          required
          value={rules.skipsCount}
          onChangeValue={(value) => {
            setRules({ ...rules, skipsCount: value });
          }}
        />
        <Radio
          label="If a game ties"
          orientation="horizontal"
          items={TIE_ITEMS}
          value={rules.tiePolicy ?? undefined}
          onValueChange={(next) => {
            setRules({
              ...rules,
              tiePolicy: next === "tieIsWin" ? "tieIsWin" : "tieIsLoss",
            });
          }}
        />
        <Radio
          label="If a game is postponed"
          items={POSTPONE_ITEMS}
          value={rules.postponementPolicy}
          onValueChange={(next) => {
            setRules({
              ...rules,
              postponementPolicy:
                next === "countAsLoss" ? "countAsLoss" : "makeUpNextRound",
            });
          }}
        />
        <p className="text-caption text-ink-secondary">
          A game that starts late, stops and restarts, or gets cut short but
          still counts as a win for somebody is a normal game. It gets graded
          like any other.
        </p>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">Who plays</h2>
        {isMaxMembersShown ? (
          <RulesNumberInput
            label="Max members"
            hint="Leave blank for unlimited."
            placeholder="Unlimited"
            min={1}
            value={rules.maxMembers}
            onChangeValue={(value) => {
              setRules({ ...rules, maxMembers: value });
            }}
          />
        ) : null}
        <RulesNumberInput
          label="Entries per member"
          min={1}
          required
          value={rules.maxEntriesPerMember}
          onChangeValue={(value) => {
            setRules({ ...rules, maxEntriesPerMember: value });
          }}
        />
        <Radio
          label="Revives per player"
          orientation="horizontal"
          items={REVIVES_ITEMS}
          value={rules.revivesAllowed}
          onValueChange={(next) => {
            if (next === "limited") {
              setRules({
                ...rules,
                revivesAllowed: "limited",
                revivesAllowedCount: rules.revivesAllowedCount ?? 1,
              });
              return;
            }
            setRules({
              ...rules,
              revivesAllowed: "unlimited",
              revivesAllowedCount: null,
            });
          }}
        />
        {rules.revivesAllowed === "limited" ? (
          <RulesNumberInput
            label="Revives allowed"
            hint="Only you can revive someone, and you confirm it each time."
            min={1}
            required
            // No `?? 1` here. The old fallback drew a 1 in a box the draft had
            // nothing in, so clearing the field could not show on screen.
            value={rules.revivesAllowedCount}
            onChangeValue={(value) => {
              setRules({ ...rules, revivesAllowedCount: value });
            }}
          />
        ) : (
          <p className="text-caption text-ink-secondary">
            Only you can revive someone, and you confirm it each time.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">Tiebreak</h2>
        <Select
          label="If the contest ends in a tie"
          items={[...TIEBREAK_ITEMS]}
          value={rules.tiebreaker}
          onValueChange={(next) => {
            const tiebreaker =
              next === "noWinner"
                ? "noWinner"
                : next === "commissionerPick"
                  ? "commissionerPick"
                  : "splitMedal";
            setRules({ ...rules, tiebreaker });
          }}
        />
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-body-strong text-ink">Points</h2>
        <RulesNumberInput
          label="Points for a win"
          hint="Every pick you win or lose scores on its own. Points decide the season trophy."
          min={0}
          required
          value={rules.winPoints}
          onChangeValue={(value) => {
            setRules({ ...rules, winPoints: value });
          }}
        />
        <RulesNumberInput
          label="Points for a loss"
          required
          value={rules.lossPoints}
          onChangeValue={(value) => {
            setRules({ ...rules, lossPoints: value });
          }}
        />
        {(rules.lossPoints ?? 0) > 0 ? (
          <div data-testid="create-pool-loss-warning">
            <InlineBanner variant="warning" message={LOSS_POINTS_WARNING} />
          </div>
        ) : null}
      </section>
    </div>
  );
};
